// ImportDXFDialog.tsx — File picker → dxfImport.ts → store.importCVPanel
// Mirrors ImportCVDialog's stage machine but for a single panel parsed
// from a DXF document. Partial imports proceed with warnings; total
// failure (no panels recovered) shows the error and disables Import.

import { useRef, useState } from 'react';
import DialogBase from './DialogBase';
import { useShallowStore } from '../store/useStore';
import { importDXFString } from '../utils/dxfImport';
import type { PanelData } from '../types/panelvar';

type Stage =
  | { kind: 'idle' }
  | { kind: 'error';   message: string }
  | { kind: 'parsed';  panels: PanelData[]; warnings: string[]; filename: string };

export default function ImportDXFDialog() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });

  const { project, importCVPanel } = useShallowStore(s => ({
    project:       s.project,
    importCVPanel: s.importCVPanel,
  }));

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result;
      if (typeof text !== 'string') {
        setStage({ kind: 'error', message: 'Could not read file.' });
        return;
      }
      const result = importDXFString(text);
      if (result.panels.length === 0) {
        setStage({ kind: 'error', message: result.warnings[0] ?? 'No panels recovered from DXF.' });
        return;
      }
      setStage({ kind: 'parsed', panels: result.panels, warnings: result.warnings, filename: file.name });
    };
    reader.onerror = () => setStage({ kind: 'error', message: 'Error reading file.' });
    reader.readAsText(file);
    e.target.value = '';
  }

  function handleImport() {
    if (stage.kind !== 'parsed') return;
    for (const panel of stage.panels) importCVPanel(panel);
  }

  const canImport = stage.kind === 'parsed';

  return (
    <DialogBase
      title="Import DXF"
      okLabel={canImport ? 'Import' : 'Close'}
      onOk={canImport ? handleImport : undefined}
    >
      <div className="dialog-row" style={{ marginBottom: 12 }}>
        <input
          ref={fileRef}
          type="file"
          accept=".dxf"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          data-testid="import-dxf-file-input"
        />
        <button
          className="dialog-btn"
          onClick={() => fileRef.current?.click()}
          data-testid="import-dxf-choose-file"
        >
          Choose DXF File…
        </button>
      </div>

      {stage.kind === 'idle' && (
        <p className="dialog-info">
          Select a <strong>.dxf</strong> file to import into the current project.
          AutoCAD R2000 (AC1015) format expected. Recognized layers: PANEL, OPENINGS,
          BLOCKOUTS, FEATURES, STRUCTURAL, NOTES (also cv-cad lowercase: perimeter,
          feature, connections, points).
        </p>
      )}

      {stage.kind === 'error' && (
        <p className="dialog-warning" data-testid="import-dxf-error">{stage.message}</p>
      )}

      {stage.kind === 'parsed' && (
        <>
          <p className="dialog-info" data-testid="import-dxf-summary">
            <strong>{stage.filename}</strong> — {stage.panels.length} panel(s) parsed
          </p>
          <p className="dialog-info">
            {project
              ? <>Panel(s) will be added to the current project: <strong>{project.name}</strong>.</>
              : <>A new project will be created to hold these panel(s).</>}
          </p>
          {stage.warnings.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <p className="dialog-warning" style={{ marginBottom: 4 }}>
                {stage.warnings.length} warning(s):
              </p>
              <ul
                data-testid="import-dxf-warnings"
                style={{ maxHeight: 160, overflowY: 'auto', fontSize: '0.85em', margin: 0, paddingLeft: 18 }}
              >
                {stage.warnings.map((w, i) => (
                  <li key={i} style={{ marginBottom: 2 }}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </DialogBase>
  );
}
